import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Logout handler - can be called from anywhere
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setShowOnboarding(false);
  }, []);

  // Listen for auth:logout event from axios interceptor
  useEffect(() => {
    const handleLogoutEvent = () => {
      console.log('Auth logout event received');
      setToken(null);
      setUser(null);
    };
    
    window.addEventListener('auth:logout', handleLogoutEvent);
    return () => window.removeEventListener('auth:logout', handleLogoutEvent);
  }, []);

  // Verify token on mount and periodically
  const verifyToken = useCallback(async (storedToken) => {
    if (!storedToken) {
      setLoading(false);
      return false;
    }
    
    try {
      const response = await axios.get('/auth/me', {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      
      setUser(response.data);
      setToken(storedToken);
      
      // Onboarding tamamlanmadıysa modal göster
      if (!response.data.onboarding_completed) {
        setShowOnboarding(true);
      }
      
      return true;
    } catch (error) {
      console.warn('Token verification failed:', error.response?.status);
      // Only clear on 401 (expired/invalid), not on network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial token verification
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    verifyToken(storedToken);
  }, [verifyToken]);

  // Periodic token verification (every 5 minutes) - prevents silent session expiry
  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        // Silent verification - don't show loading
        axios.get('/auth/me', {
          headers: { Authorization: `Bearer ${storedToken}` }
        }).then(response => {
          // Update user data silently (in case of role/permission changes)
          setUser(response.data);
          
          // Token refresh: Yeni token varsa context'i de güncelle
          const newToken = response.headers['x-new-token'];
          if (newToken && newToken !== storedToken) {
            console.log('Token refreshed in AuthContext');
            setToken(newToken);
          }
        }).catch(error => {
          if (error.response?.status === 401) {
            console.warn('Session expired during periodic check');
            logout();
          }
          // Ignore network errors - keep session
        });
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [token, logout]);

  // Sync across browser tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          // Token removed in another tab
          setToken(null);
          setUser(null);
        } else if (e.newValue !== token) {
          // Token changed in another tab - verify it
          verifyToken(e.newValue);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token, verifyToken]);

  const login = async (email, password) => {
    const response = await axios.post('/auth/login', { email, password });
    const { token: authToken, user: userData } = response.data;
    localStorage.setItem('token', authToken);
    setToken(authToken);
    setUser(userData);
    
    // Onboarding tamamlanmadıysa modal göster
    if (!userData.onboarding_completed) {
      setShowOnboarding(true);
    }
    
    return userData;
  };

  const register = async (email, password, name, referralCode = null, planType = 'free', billingCycle = 'monthly') => {
    const payload = { email, password, name, plan_type: planType, billing_cycle: billingCycle };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    const response = await axios.post('/auth/register', payload);
    const { token: authToken, user: userData } = response.data;
    localStorage.setItem('token', authToken);
    setToken(authToken);
    setUser(userData);
    
    // Yeni kullanıcı - onboarding göster
    setShowOnboarding(true);
    
    return userData;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      await verifyToken(storedToken);
    }
  };

  const closeOnboarding = () => {
    setShowOnboarding(false);
  };

  const completeOnboarding = (updatedUser) => {
    // Onboarding tamamlandığında user state'ini güncelle
    setUser(prev => ({
      ...prev,
      onboarding_completed: true,
      onboarding_goal: updatedUser?.onboarding_goal || prev?.onboarding_goal
    }));
    setShowOnboarding(false);
  };

  // isPro computed value - tutarlı kontrol için merkezi hesaplama
  const isPro = user?.is_pro === true || 
    (user?.membership?.plan_type === 'pro' && user?.membership?.status === 'active') || 
    user?.role === 'admin';

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    showOnboarding,
    closeOnboarding,
    completeOnboarding,
    updateUser,
    refreshUser,
    isPro
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
